import React, { useEffect, useState } from 'react';
import { TFormField, TPDCEDialogProps } from '../Types/party.types';
import { CloseIcon } from 'assests/GmsIcons';
import { styles } from 'Pages/GMS/ISR/UI-Model/utils/styles';

export const CustomDialog = ({ isOpen, onClose, title, fields, onSave, onCancel, saveButtonText = "Save", cancelButtonText = "Cancel", isLoading = false }: TPDCEDialogProps) => {
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [hasInitialized, setHasInitialized] = useState(false);

  useEffect(() => {
    if (isOpen && !hasInitialized) {
      const initialData: Record<string, any> = {};
      fields.forEach(field => {
        if (field.defaultValue !== undefined) {
          initialData[field.name] = field.defaultValue;
        } else {
          initialData[field.name] = field.type === 'checkbox' ? false : '';
        }
      });
      setFormData(initialData);
      setHasInitialized(true);
    } else if (!isOpen) {
      setHasInitialized(false);
    }
  }, [fields, isOpen, hasInitialized]);

  const handleInputChange = (name: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = () => {
    onSave(formData);
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      onClose();
    }
    if (!isLoading) {
      setFormData({});
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
      setFormData({});
    }
  };

  if (!isOpen) return null;

  const renderField = (field: TFormField) => {
    const value = formData[field.name] || '';

    switch (field.type) {
      case 'text':
        return (
          <input
            type="text"
            placeholder={field.placeholder || "Enter"}
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            className={`${styles.input} placeholder:tw-text-slate-500 placeholder:tw-text-sm placeholder:tw-leading-tight`}
            disabled={isLoading}
          />
        );

      case 'select':
        return (
          <div className={styles.selectContainer}>
            <select
              value={value}
              onChange={(e) => handleInputChange(field.name, e.target.value)}
              className={`${styles.select}`}
              disabled={isLoading}
            >
              <option value="">Select</option>
              {field.options?.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <div className={styles.selectIcon}>
              <svg className="tw-w-4 tw-h-4 tw-text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd"></path>
              </svg>
            </div>
          </div>
        );

      case 'textarea':
        return (
          <textarea
            placeholder={field.placeholder || "Enter"}
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            className={`${styles.input}`}
            rows={3}
            disabled={isLoading}
          />
        );

      case 'checkbox':
        return (
          <div className="tw-flex tw-items-center tw-gap-3">
            <input
              type="checkbox"
              checked={value || false}
              onChange={(e) => handleInputChange(field.name, e.target.checked)}
              className="tw-w-4 tw-h-4 tw-text-blue-600 tw-border-gray-300 tw-rounded focus:tw-ring-blue-500 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
              disabled={isLoading || field.disabled}
            />
            <span className="tw-text-gray-700">
              {renderCheckboxLabel(field.placeholder || field.label)}
            </span>
          </div>
        );

      default:
        return null;
    }
  };

  const renderCheckboxLabel = (text: string) => {
    const parts = text.split(/('.*?')/);

    return parts.map((part, index) => {
      if (part.startsWith("'") && part.endsWith("'")) {
        return (
          <span key={index} className="tw-font-semibold">
            {part}
          </span>
        );
      }
      return part;
    });
  };

  return (
    <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-z-50">
      <div className="tw-bg-white tw-rounded tw-shadow-xl tw-w-full tw-max-w-md tw-mx-4">
        {/* Header */}
        <div className="tw-flex tw-items-center tw-justify-between tw-p-3 tw-border-b tw-border-[#D1D1D6]">
          <h2 className="tw-text-sm tw-font-semibold tw-text-[#091E42]">{title}</h2>
          <button
            onClick={handleClose}
            className="tw-text-gray-400 hover:tw-text-gray-600 tw-text-2xl tw-leading-none disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
            disabled={isLoading}
          >
            <CloseIcon />
          </button>
        </div>

        {/* Form Fields */}
        <div className="tw-p-6 tw-space-y-3">
          {fields.map((field) => (
            <div key={field.name}>
              <label className="tw-block tw-text-sm tw-font-normal tw-text-black tw-mb-1">
                {field.label}
                {field.required && <span className="tw-text-red-500 tw-ml-1">*</span>}
              </label>
              {renderField(field)}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="tw-flex tw-justify-end tw-gap-3 tw-py-3 tw-pe-6">
          <button
            onClick={handleCancel}
            className="popup-button-outline disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
            disabled={isLoading}
          >
            {cancelButtonText}
          </button>
          <button
            onClick={handleSave}
            className="popup-button-filled disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-min-w-[80px]"
            disabled={isLoading}
          >
            {isLoading ? (
              <span className="tw-flex tw-items-center tw-justify-center">
                <svg className="tw-animate-spin tw-h-4 tw-w-4 tw-mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="tw-opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="tw-opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Creating...
              </span>
            ) : (
              saveButtonText
            )}
          </button>
        </div>
      </div>
    </div>
  );
};